import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxOpen, faUsers } from '@fortawesome/free-solid-svg-icons';
import Button from '../../components/Button';
import Carousel from '../../components/Carousel';
import { apiUrl } from '../../utils/api';
import { localizePath } from '../../utils/language';
import './Products.css';

const Products = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [activeCategory, setActiveCategory] = useState(null);
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [isLoadingCategories, setIsLoadingCategories] = useState(false);
    const [isLoadingProducts, setIsLoadingProducts] = useState(false);
    const escapeRegex = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const stripLeadingIcon = (label, icon) => {
        if (!label || !icon) return label || '';
        const pattern = new RegExp(`^${escapeRegex(icon)}\\s*`);
        return label.replace(pattern, '');
    };

    useEffect(() => {
        const controller = new AbortController();

        const loadCategories = async () => {
            setIsLoadingCategories(true);
            try {
                const response = await fetch(apiUrl('/api/v1/catalog/categories/'), {
                    signal: controller.signal
                });

                if (!response.ok) {
                    throw new Error(`Categories request failed: ${response.status}`);
                }

                const data = await response.json();
                const results = Array.isArray(data?.results)
                    ? [...data.results].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                    : [];
                const categoriesWithSlug = results.filter((item) => item?.slug);

                setCategories(categoriesWithSlug);
                setActiveCategory((prev) => {
                    if (prev && categoriesWithSlug.some((category) => category.slug === prev)) {
                        return prev;
                    }
                    return categoriesWithSlug[0]?.slug ?? null;
                });
            } catch (error) {
                if (error.name === 'AbortError') {
                    return;
                }
                console.error(error.message || 'Failed to load categories');
                setCategories([]);
                setActiveCategory(null);
            } finally {
                setIsLoadingCategories(false);
            }
        };

        loadCategories();

        return () => controller.abort();
    }, []);

    useEffect(() => {
        if (!activeCategory) {
            setProducts([]);
            setIsLoadingProducts(false);
            return undefined;
        }

        const controller = new AbortController();

        const loadProducts = async () => {
            setIsLoadingProducts(true);
            try {
                const response = await fetch(
                    apiUrl(`/api/v1/catalog/products/category/${encodeURIComponent(activeCategory)}/`),
                    { signal: controller.signal }
                );

                if (!response.ok) {
                    throw new Error(`Products request failed: ${response.status}`);
                }

                const data = await response.json();
                const results = Array.isArray(data?.results)
                    ? [...data.results].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                    : [];
                setProducts(results);
            } catch (error) {
                if (error.name === 'AbortError') {
                    return;
                }
                console.error(error.message || 'Failed to load products');
                setProducts([]);
            } finally {
                setIsLoadingProducts(false);
            }
        };

        loadProducts();

        return () => controller.abort();
    }, [activeCategory]);

    const hasProducts = products.length > 0;
    const isLoading = isLoadingCategories || isLoadingProducts;

    const getLocalizedValue = (item, key) => {
        if (i18n.language === 'uz' && item?.[`${key}_uz`]) {
            return item[`${key}_uz`];
        }
        if (i18n.language === 'en' && item?.[`${key}_en`]) {
            return item[`${key}_en`];
        }
        return item?.[key] || item?.[`${key}_en`] || item?.[`${key}_uz`] || '';
    };

    const getCategoryLabel = (category) => {
        if (i18n.language === 'uz' && category.name_uz) {
            return stripLeadingIcon(category.name_uz, category.icon);
        }
        if (i18n.language === 'en' && category.name_en) {
            return stripLeadingIcon(category.name_en, category.icon);
        }
        const label = category.name || category.name_en || category.name_uz || '';
        return stripLeadingIcon(label, category.icon);
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.1 }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 }
    };

    const ProductCard = ({ item }) => {
        const slug = item?.slug;
        const handleLearnMore = () => {
            if (slug) {
                navigate(localizePath(i18n.language, `/blog/posts/${encodeURIComponent(slug)}`));
            }
        };

        return (
            
            <div className="product-card product-primary">
                <div className="product-icon-wrapper">
                    {item.icon ? (
                        <span className="product-icon" aria-hidden="true">
                            {item.icon}
                        </span>
                    ) : (
                        <span className="product-icon" aria-hidden="true">
                            <FontAwesomeIcon icon={faBoxOpen} />
                        </span>
                    )}
                </div>
                <div className="product-content">
                    <h3 className="product-title">{getLocalizedValue(item, 'name')}</h3>
                    <p className="product-description">
                        {getLocalizedValue(item, 'short_description')}
                    </p>
                    <p className="product-for-whom">
                        <span className="for-whom-label" aria-hidden="true">
                            <FontAwesomeIcon icon={faUsers} />
                        </span>
                        {getLocalizedValue(item, 'target_audience')}
                    </p>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={handleLearnMore}
                    className="product-btn"
                    disabled={!slug}
                >
                    {t('products.learn_more')}
                </Button>
            </div>
        );
    };

    return (
        <section id="products" className="section products-section">
            <div className="container">
                {/* Header */}
                <motion.div
                    className="section-header"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                >
                    <h2 className="section-title">{t('products.title')}</h2>
                    <p className="section-subtitle">{t('products.subtitle')}</p>
                </motion.div>

                {/* Category Tabs */}
                <div className="category-tabs">
                    {categories.map((category) => {
                        const label = getCategoryLabel(category);
                        const isActive = activeCategory === category.slug;
                        return (
                            <button
                                key={category.id ?? category.slug}
                                className={`category-tab ${isActive ? 'active' : ''}`}
                                onClick={() => {
                                    setActiveCategory(category.slug);
                                    setProducts([]);
                                }}
                                type="button"
                            >
                                {category.icon ? `${category.icon} ` : ''}
                                {label}
                            </button>
                        );
                    })}
                </div>

                {/* Desktop Grid */}
                <motion.div
                    className="products-grid products-grid--desktop"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    key={`desktop-${activeCategory}`}
                >
                    {isLoading ? (
                        <motion.div variants={itemVariants}>
                            <p className="product-loading">Loading...</p>
                        </motion.div>
                    ) : hasProducts ? (
                        products.map((item) => (
                            <motion.div key={item.id ?? item.slug} variants={itemVariants}>
                                <ProductCard item={item} />
                            </motion.div>
                        ))
                    ) : (
                        <motion.div variants={itemVariants}>
                            <p className="product-loading">No products available.</p>
                        </motion.div>
                    )}
                </motion.div>

                {/* Mobile Carousel */}
                <div className="products-carousel products-carousel--mobile" key={`mobile-${activeCategory}`}>
                    {isLoading ? (
                        <div className="product-loading">Loading...</div>
                    ) : hasProducts ? (
                        <Carousel showArrows={false}>
                            {products.map((item) => (
                                <ProductCard key={item.id ?? item.slug} item={item} />
                            ))}
                        </Carousel>
                    ) : (
                        <div className="product-loading">No products available.</div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default Products;
